// Tiny markdown renderer for legal & CMS copy.
// Supports:  paragraphs, "- " bullets, "1. " ordered lists, **bold**, [text](url).
// Internal links ("/…") render as React Router <Link>; others as <a>.
// Bullets and ordered items don't require a blank line — a line starting with
// "- " or "1. " starts a new list block automatically.
import React from "react";
import { Link } from "react-router-dom";

const INLINE_RE = /(\*\*[^*]+\*\*|\[[^\]]+\]\([^)]+\))/g;

function renderInline(text, keyBase) {
  const parts = text.split(INLINE_RE).filter((s) => s !== "");
  return parts.map((p, i) => {
    const key = `${keyBase}-${i}`;
    if (p.startsWith("**") && p.endsWith("**")) {
      return <strong key={key} className="text-zinc-200">{p.slice(2, -2)}</strong>;
    }
    const link = p.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
    if (link) {
      const [, label, href] = link;
      if (href.startsWith("/")) return <Link key={key} to={href} className="text-rose-400 underline">{label}</Link>;
      return <a key={key} href={href} className="text-rose-400 underline" target={href.startsWith("http") ? "_blank" : undefined} rel="noreferrer">{label}</a>;
    }
    return <React.Fragment key={key}>{p}</React.Fragment>;
  });
}

// Turn one raw block (already split by blank lines) into inline groups.
function renderBlock(raw, blockKey) {
  const lines = raw.split("\n").map((l) => l.trimEnd()).filter((l) => l.length > 0);
  const groups = []; // { kind: 'p'|'ul'|'ol', lines: string[] }
  for (const line of lines) {
    const isBullet = /^-\s+/.test(line);
    const isOrdered = /^\d+\.\s+/.test(line);
    const kind = isBullet ? "ul" : isOrdered ? "ol" : "p";
    const clean = isBullet ? line.replace(/^-\s+/, "") : isOrdered ? line.replace(/^\d+\.\s+/, "") : line;
    const last = groups[groups.length - 1];
    // Paragraph lines merge together with a space; list items each get their own entry.
    if (last && last.kind === kind && kind === "p") {
      last.lines[0] = `${last.lines[0]} ${clean}`;
    } else if (last && last.kind === kind && kind !== "p") {
      last.lines.push(clean);
    } else {
      groups.push({ kind, lines: [clean] });
    }
  }
  return groups.map((g, gi) => {
    const k = `${blockKey}-${gi}`;
    if (g.kind === "ul") return (
      <ul key={k} className="list-disc pl-5 space-y-1">
        {g.lines.map((l, i) => <li key={i}>{renderInline(l, `${k}-${i}`)}</li>)}
      </ul>
    );
    if (g.kind === "ol") return (
      <ol key={k} className="list-decimal pl-5 space-y-1">
        {g.lines.map((l, i) => <li key={i}>{renderInline(l, `${k}-${i}`)}</li>)}
      </ol>
    );
    return <p key={k}>{renderInline(g.lines[0], k)}</p>;
  });
}

export function MiniMarkdown({ text, className = "" }) {
  if (!text) return null;
  const blocks = text.replace(/\r\n/g, "\n").split(/\n\s*\n/);
  return (
    <div className={`text-sm text-zinc-400 leading-relaxed space-y-2 ${className}`}>
      {blocks.map((raw, bi) => (
        <React.Fragment key={bi}>{renderBlock(raw, `b${bi}`)}</React.Fragment>
      ))}
    </div>
  );
}
